import React, { useEffect } from 'react';
import { IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonIcon, IonLabel } from '@ionic/react';
import { Route, Redirect } from 'react-router';
import { calendarOutline, peopleOutline, qrCode, peopleCircleOutline } from 'ionicons/icons';
import SchedulePage from './SchedulePage';
import SpeakerList from './SpeakerList';
import SpeakerDetail from './SpeakerDetail';
import SessionDetail from './SessionDetail';
import MapView from './MapView';
import MapIframePage from './MapIframePage';
import About from './About';
import NetworkingPage from './NetworkingPage';
import NetworkingContacts from './NetworkingContacts';
import { connect } from '../data/connect';
import { applyDeviceSpecificStyles } from '../utils/deviceDetection';
import { getEnabledBottomTabs, isFeatureEnabled } from '../config/app.config';

interface StateProps {
  hasScheduleData: boolean;
  hasSpeakersData: boolean;
}

interface MainTabsProps extends StateProps { }

const MainTabs: React.FC<MainTabsProps> = ({ hasScheduleData, hasSpeakersData }) => {

  // Aplicar detección de dispositivo al montar el componente
  useEffect(() => {
    applyDeviceSpecificStyles();
  }, []);

  // Obtener tabs habilitados desde la configuración
  const enabledTabs = getEnabledBottomTabs();

  return (
    <IonTabs>
      <IonRouterOutlet>
        {/* Redirigir a la agenda solo si hay datos, de lo contrario ir al gafete */}
        <Redirect exact path="/tabs" to={hasScheduleData ? "/tabs/schedule" : "/tabs/about"} />
        {/*
          Using the render method prop cuts down the number of renders your components will have due to route changes.
          Use the component prop when your component depends on the RouterComponentProps passed in automatically.
        */}
        <Route path="/tabs/schedule" render={() => <SchedulePage />} exact={true} />
        <Route path="/tabs/speakers" render={() => <SpeakerList />} exact={true} />
        <Route path="/tabs/speakers/sessions/:id" component={SessionDetail} />
        <Route path="/tabs/speakers/:id" component={SpeakerDetail} exact={true} />
        <Route path="/tabs/schedule/:id" component={SessionDetail} />
        <Route path="/tabs/map" render={() => <MapView />} exact={true} />
        <Route path="/tabs/map-iframe" render={() => <MapIframePage />} exact={true} />
        <Route path="/tabs/about" render={() => <About />} exact={true} />

        {/* Rutas de Networking */}
        {isFeatureEnabled('networking') && (
          <>
            <Route path="/tabs/networking" render={() => <NetworkingPage />} exact={true} />
            <Route path="/tabs/networking/contacts" render={() => <NetworkingContacts />} exact={true} />
          </>
        )}
      </IonRouterOutlet>
      <IonTabBar slot="bottom" className="new-tab-bar">
        {enabledTabs.map((tab) => {
          // Lógica condicional para mostrar tabs según disponibilidad de datos
          if (tab.key === 'schedule' && !hasScheduleData) return null;
          if (tab.key === 'speakers' && !hasSpeakersData) return null;

          // Mapear iconos
          const getIcon = (tabKey: string) => {
            switch (tabKey) {
              case 'schedule': return calendarOutline;
              case 'speakers': return peopleOutline;
              case 'networking': return peopleCircleOutline;
              case 'badge': return qrCode;
              default: return qrCode;
            }
          };

          // Mapear rutas
          const getHref = (tabKey: string) => {
            switch (tabKey) {
              case 'badge': return '/tabs/about';
              default: return `/tabs/${tabKey}`;
            }
          };

          return (
            <IonTabButton
              key={tab.key}
              tab={tab.key}
              href={getHref(tab.key)}
              className="new-tab-button"
            >
              <IonIcon icon={getIcon(tab.key)} className="white-icon" />
              <IonLabel>{tab.label}</IonLabel>
            </IonTabButton>
          );
        })}
      </IonTabBar>
    </IonTabs>
  );
};

export default connect<{}, StateProps, {}>({
  mapStateToProps: (state) => ({
    hasScheduleData: state.data.schedule && state.data.schedule.groups && state.data.schedule.groups.length > 0,
    hasSpeakersData: state.data.speakers && state.data.speakers.length > 0
  }),
  component: MainTabs
});